import React, { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, Redirect, useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isEqual, startCase } from 'lodash'
import moment from 'moment-timezone'
import { Helmet } from 'react-helmet'
import { validate as isValidUUID } from 'uuid'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  Spinner,
  Text
} from '@chakra-ui/react'
import { capabilities } from '@homevest/utils'

import type {
  LeaseCreationDraftResponse,
  LeaseCreationFormValues
} from '@homevest/types/rental-applications/lease-creation'

import { hasCapability } from 'lib/admin-perms'
import { leaseCreationApi } from 'lib/lease-creation-api'
import { StoreState } from 'store'
import { EditableLeaseValues, LeaseDraftForm, LeaseReview } from './components'

const { APPLICATION_REVIEWER } = capabilities.CAPABILITY_TYPES

type DisplayError = {
  message: string
  fieldErrors?: Record<string, string>
}

const copyValues = (values: LeaseCreationFormValues | EditableLeaseValues) =>
  ({ ...values } as EditableLeaseValues)

const currency = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const MONEY_INPUT_PATTERN = /^\$?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{0,2})?|\.\d{1,2})$/

const normalizeMoneyValues = (
  source: EditableLeaseValues,
  minimumInitialPayment = 500
): { values?: LeaseCreationFormValues; fieldErrors: Record<string, string> } => {
  const values = copyValues(source)
  const fieldErrors: Record<string, string> = {}
  const normalize = (field: string, value: unknown) => {
    const text = String(value ?? '').trim()
    const normalizedText = text.replace(/[$,]/g, '')
    const numericValue = Number(normalizedText)
    if (
      !MONEY_INPUT_PATTERN.test(text) ||
      !Number.isFinite(numericValue) ||
      numericValue < 0 ||
      numericValue > 99_999_999.99
    ) {
      fieldErrors[field] = 'Enter a dollar amount with up to 2 decimal places.'
      return value
    }
    return numericValue.toFixed(2)
  }

  const fields = ['utilityFee', 'petRent', 'upfrontPetFees', 'concessionTotal']
  if (values.kind === 'standard') {
    fields.push('amenityFee', 'otherMonthly', 'initialOptionPremium')
  } else if (values.kind === 'sandalwood') {
    fields.push('amenityFee', 'otherMonthly')
    if (values.guarantor === false) {
      fields.push('securityDepositAmount')
    }
  } else if (values.kind === 'kcpoa') {
    fields.push(
      'amenityFee',
      'otherMonthly',
      'initialOptionFee',
      'monthlyOptionFee',
      'purchasePrice'
    )
  }
  fields.forEach((field) => {
    values[field] = normalize(field, values[field])
  })

  if (Array.isArray(values.concessions)) {
    values.concessions = values.concessions.map((concession, index) => ({
      ...concession,
      amount: normalize(`concessions.${index}.amount`, concession.amount)
    }))
  }

  const minimumTerm = values.kind === 'kcpoa' ? 3 : 1
  if (
    !Number.isInteger(values.initialLeaseTermMonths) ||
    values.initialLeaseTermMonths < minimumTerm ||
    values.initialLeaseTermMonths > 60
  ) {
    fieldErrors.initialLeaseTermMonths = `Enter a whole number between ${minimumTerm} and 60.`
  }
  if (
    !moment(values.occupancyDate, 'YYYY-MM-DD', true).isValid() ||
    values.occupancyDate < moment().tz('America/New_York').format('YYYY-MM-DD')
  ) {
    fieldErrors.occupancyDate = 'Choose today or a later date.'
  }
  if (
    !Number.isFinite(values.stepUpPercentagePoints) ||
    values.stepUpPercentagePoints < 0 ||
    values.stepUpPercentagePoints > 30
  ) {
    fieldErrors.stepUpPercentagePoints = 'Enter a percentage between 0 and 30.'
  }

  if (values.kind === 'kcpoa') {
    if (!fieldErrors.initialOptionFee && Number(values.initialOptionFee) < minimumInitialPayment) {
      fieldErrors.initialOptionFee = `Enter at least ${currency(minimumInitialPayment)}.`
    }
    if (Number(values.purchasePrice) <= 1) {
      fieldErrors.purchasePrice = 'Enter a purchase price greater than $1.00.'
    }
  }

  const concessionTotal = Number(values.concessionTotal)
  const concessions = Array.isArray(values.concessions) ? values.concessions : []
  const concessionSum = concessions.reduce(
    (total, concession) => total + Number(concession.amount),
    0
  )
  if (Math.round((concessionTotal - concessionSum) * 100) !== 0) {
    fieldErrors.concessionTotal = 'The concession total must match the rows below.'
  }
  if (concessionTotal > 0 && !values.concessionDescription?.trim()) {
    fieldErrors.concessionDescription = 'Add a short description for the concession.'
  }
  concessions.forEach((concession, index) => {
    if (Number(concession.amount) > 0 && !moment(concession.month, 'YYYY-MM', true).isValid()) {
      fieldErrors[`concessions.${index}.month`] = 'Choose the concession month.'
    }
  })

  return Object.keys(fieldErrors).length
    ? { fieldErrors }
    : { values: values as LeaseCreationFormValues, fieldErrors }
}

const displayError = (error: unknown): DisplayError => ({
  message:
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
      ? error.message
      : typeof error === 'string'
      ? error
      : 'Lease creation failed unexpectedly.'
})

const buildAgreementPayload = (
  draft: LeaseCreationDraftResponse,
  values: LeaseCreationFormValues
): Record<string, unknown> => {
  const amount = (value: string | undefined) => Number(value)
  const payload: Record<string, unknown> = {
    initial_lease_term: values.initialLeaseTermMonths,
    occupancy_date: values.occupancyDate,
    utility_fee: amount(values.utilityFee),
    pet_rent: amount(values.petRent),
    upfront_pet_fees: amount(values.upfrontPetFees),
    step_up_increase_percentage: values.stepUpPercentagePoints
  }

  if (values.kind === 'standard') {
    Object.assign(payload, {
      initial_option_premium: amount(values.initialOptionPremium),
      monthly_option_premium: amount(draft.serverOwned.monthlyOption),
      amenity_fee: amount(values.amenityFee),
      other_monthly: amount(values.otherMonthly),
      concession_amount_total: amount(values.concessionTotal),
      concession_description: values.concessionDescription ?? ''
    })
  } else if (values.kind === 'sandalwood') {
    Object.assign(payload, {
      security_deposit_amount: values.guarantor ? 0 : amount(values.securityDepositAmount),
      amenity_fee: amount(values.amenityFee),
      other_monthly: amount(values.otherMonthly),
      concession_amount_total: amount(values.concessionTotal),
      concession_description: values.concessionDescription ?? ''
    })
  } else {
    Object.assign(payload, {
      amenity_fee: amount(values.amenityFee),
      initial_option_fee: amount(values.initialOptionFee),
      monthly_option_fee: amount(values.monthlyOptionFee),
      other_monthly: amount(values.otherMonthly),
      deposit_credit: 0,
      purchase_price: amount(values.purchasePrice),
      concession_amount_total: amount(values.concessionTotal),
      concession_description: values.concessionDescription ?? ''
    })
  }

  values.concessions.forEach((concession, index) => {
    if (Number(concession.amount) === 0) {
      return
    }
    payload[`concession_amount_${index + 1}`] = amount(concession.amount)
    payload[`concession_month_${index + 1}`] = `${concession.month}-15`
  })

  draft.participants.occupants.forEach((occupant, index) => {
    const prefix = index === 0 ? 'occupant' : `occupant_${index + 1}`
    payload[`${prefix}_name`] = occupant.name
    payload[`${prefix}_age`] = occupant.age
    payload[`${prefix}_relation`] = startCase(occupant.relationship)
  })
  draft.participants.vehicles.forEach((vehicle, index) => {
    const prefix = index === 0 ? 'car' : `car_${index + 1}`
    payload[`${prefix}_license`] = vehicle.licensePlate.toUpperCase()
    payload[`${prefix}_make`] = vehicle.make
    payload[`${prefix}_model`] = vehicle.model
    payload[`${prefix}_year`] = vehicle.year
  })
  draft.participants.pets.forEach((pet, index) => {
    const prefix = index === 0 ? 'pet' : `pet_${index + 1}`
    payload[`${prefix}_name`] = pet.name
    payload[`${prefix}_type`] = startCase(pet.type)
    payload[`${prefix}_color`] = startCase(pet.color)
    payload[`${prefix}_weight`] = pet.weightInPounds
  })

  return payload
}

const LeaseCreationScreen = () => {
  const admin = useSelector((state: StoreState) => state.admin)
  const history = useHistory()
  const location = useLocation()
  const canReviewApplications = hasCapability(admin as any, APPLICATION_REVIEWER)

  const queryApplicationId = new URLSearchParams(location.search).get('applicationId')
  const selectedApplicationId =
    queryApplicationId && isValidUUID(queryApplicationId) ? queryApplicationId : undefined

  const [draft, setDraft] = useState<LeaseCreationDraftResponse | null>(null)
  const [values, setValues] = useState<EditableLeaseValues | null>(null)
  const [reviewValues, setReviewValues] = useState<LeaseCreationFormValues | null>(null)
  const [pageError, setPageError] = useState<DisplayError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [submitLocked, setSubmitLocked] = useState(false)
  const submitInFlightRef = React.useRef(false)

  const resetSelectedState = useCallback(() => {
    setDraft(null)
    setValues(null)
    setReviewValues(null)
    setPageError(null)
    setSubmitLocked(false)
  }, [])

  useEffect(() => {
    if (!selectedApplicationId || !canReviewApplications) {
      resetSelectedState()
      return
    }

    let cancelled = false
    resetSelectedState()
    setIsLoading(true)

    const loadApplication = async () => {
      try {
        const nextDraft = await leaseCreationApi.getDraft(selectedApplicationId)
        if (cancelled) {
          return
        }
        setDraft(nextDraft)
        setValues(copyValues(nextDraft.values))
      } catch (error) {
        if (!cancelled) {
          setPageError(displayError(error))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadApplication()
    return () => {
      cancelled = true
    }
  }, [canReviewApplications, resetSelectedState, selectedApplicationId])

  const updateValues = (nextValues: EditableLeaseValues) => {
    if (!isEqual(values, nextValues)) {
      setSubmitLocked(false)
    }
    setValues(nextValues)
    setReviewValues(null)
    if (pageError?.fieldErrors) {
      const nextFieldErrors = normalizeMoneyValues(
        nextValues,
        Number(draft?.serverOwned.minimumInitialPayment ?? 500)
      ).fieldErrors
      setPageError(
        Object.keys(nextFieldErrors).length
          ? {
              message: 'Review the highlighted fields.',
              fieldErrors: nextFieldErrors
            }
          : null
      )
    } else {
      setPageError(null)
    }
  }

  const reviewLease = () => {
    if (!values || !draft) {
      return
    }

    const signers = draft.participants.tenants
    const tenantSigners = signers.filter((signer) => signer.role !== 'cosigner')
    const cosigners = signers.filter((signer) => signer.role === 'cosigner')
    const primarySigners = signers.filter((signer) => signer.role === 'primary')
    if (tenantSigners.length === 0 || primarySigners.length !== 1) {
      setPageError({
        message:
          'This lease needs exactly one primary tenant. Update the application participants before continuing.'
      })
      return
    }
    if (tenantSigners.length > 6 || cosigners.length > 1) {
      setPageError({
        message:
          'This lease supports up to 6 tenants and 1 cosigner. Update the application participants before continuing.'
      })
      return
    }
    if (
      signers.some(
        (signer) =>
          !signer.name.trim() ||
          !/^\S+@\S+\.\S+$/.test(signer.email.trim()) ||
          (values.kind === 'kcpoa' && signer.name.trim().split(/\s+/).length < 2)
      )
    ) {
      setPageError({
        message:
          'Every lease signer needs a full name and valid email. Update the application participants before continuing.'
      })
      return
    }

    if (draft.participants.occupants.length > 6) {
      setPageError({
        message:
          'This lease template supports up to 6 occupants. Update Required Lease Details before continuing.'
      })
      return
    }
    if (draft.participants.vehicles.length > 4) {
      setPageError({
        message:
          'This lease template supports up to 4 vehicles. Update Required Lease Details before continuing.'
      })
      return
    }
    if (draft.participants.pets.length > 4) {
      setPageError({
        message:
          'This lease template supports up to 4 pets. Update Required Lease Details before continuing.'
      })
      return
    }

    const normalized = normalizeMoneyValues(
      values,
      Number(draft.serverOwned.minimumInitialPayment ?? 500)
    )
    if (!normalized.values) {
      setPageError({
        message: 'Review the highlighted fields.',
        fieldErrors: normalized.fieldErrors
      })
      return
    }

    const requestedValues = normalized.values
    setValues(copyValues(requestedValues))
    setReviewValues(requestedValues)
    if (!submitLocked) {
      setPageError(null)
    }
  }

  const submitLease = async () => {
    if (
      !selectedApplicationId ||
      !draft ||
      !reviewValues ||
      isLoading ||
      submitLocked ||
      submitInFlightRef.current
    ) {
      return
    }

    submitInFlightRef.current = true
    setSubmitLocked(true)
    setIsLoading(true)
    setPageError(null)
    try {
      await leaseCreationApi.submit(
        selectedApplicationId,
        buildAgreementPayload(draft, reviewValues)
      )
      history.push(`/applications/${selectedApplicationId}`)
    } catch (error) {
      setPageError(displayError(error))
    } finally {
      submitInFlightRef.current = false
      setIsLoading(false)
    }
  }

  const fieldErrors = pageError?.fieldErrors ?? {}

  if (!canReviewApplications) {
    return (
      <Container maxW='container.lg' py={8}>
        <Alert status='error'>
          <AlertIcon />
          <Box>
            <AlertTitle>Application reviewer access required</AlertTitle>
            <AlertDescription>You are not authorized to create leases.</AlertDescription>
          </Box>
        </Alert>
      </Container>
    )
  }

  if (!selectedApplicationId) {
    return <Redirect to='/applications' />
  }

  return (
    <Container maxW='container.xl' py={8}>
      <Helmet>
        <title>Create lease</title>
      </Helmet>
      <Flex
        justify='space-between'
        align={{ base: 'stretch', md: 'start' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={6}
      >
        <Box>
          <Heading>Create lease</Heading>
          <Text color='gray.600'>Confirm the terms, then create the signature request.</Text>
        </Box>
        <Button
          as={RouterLink}
          to={`/applications/${selectedApplicationId}`}
          variant='outline'
          leftIcon={<ArrowLeftIcon className='h-4 w-4' />}
          width={{ base: 'full', md: 'auto' }}
        >
          Back to application
        </Button>
      </Flex>

      {isLoading && !draft && selectedApplicationId && (
        <Center minH='300px'>
          <Spinner size='xl' />
        </Center>
      )}

      {reviewValues && draft && (
        <LeaseReview
          draft={draft}
          error={pageError?.message}
          values={reviewValues}
          isLoading={isLoading}
          isSubmitDisabled={submitLocked}
          onBack={() => setReviewValues(null)}
          onSubmit={submitLease}
        />
      )}

      {!reviewValues && draft && values && (
        <LeaseDraftForm
          draft={draft}
          values={values}
          fieldErrors={fieldErrors}
          isLoading={isLoading}
          onChange={updateValues}
          onReview={reviewLease}
        />
      )}

      {pageError && (!reviewValues || !draft) && (
        <Alert status='error' mt={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>
              {pageError.fieldErrors ? 'Check the highlighted fields' : 'Lease creation error'}
            </AlertTitle>
            <AlertDescription>{pageError.message}</AlertDescription>
          </Box>
        </Alert>
      )}
    </Container>
  )
}

export default LeaseCreationScreen
